<script setup lang="ts">
  import { computed } from "vue";
  import MarketoInputSyncWrapper from "./marketo-input-sync-wrapper.vue";
  import type { MarketoUserInputMeta } from "../types/definitions/inputs/marketo-input-meta.js";
  import type { InputState } from "../composables/use-input-state.js";
  import { isBoolean, type Nil } from "@mcwd/typescript-type-guards";
  import { FormLabelKeysEnum } from "../locale/translation-keys/form-label-keys.js";
  import { TranslationNamespaceEnum } from "../locale/settings/translation-namespaces.js";
  import { useVModel, refDefault } from "@vueuse/core";
  import { useTranslation } from "../composables/use-translation.js";

  export interface EclipseInputProps {
    formInputMeta: MarketoUserInputMeta,
    inputState: InputState
    modelValue: string | string[] | number | boolean | Nil;
    dropdownOptionKeys?: readonly string[] | Nil,
    dropdownTranslationsNamespace?: string | undefined;
    withoutSyncWrapper?: boolean
  }

  const { translate } = useTranslation();

  const props = defineProps<EclipseInputProps>();

  const emit = defineEmits<(e: 'update:modelValue', modelValue: EclipseInputProps["modelValue"]) => void>();

  const proxyModelValue = refDefault(useVModel(props, 'modelValue', emit), null);

  const parentWrapperClasses = computed(() => {
    return [{ error: props.inputState.showError, done: props.inputState.done, required: props.inputState.required, notEmpty: !props.inputState.isEmpty }];
    // notEmpty is used for textarea styles only
  });

  const commonInputProps = computed(() => {
    return {
      id: props.formInputMeta.name,
      name: props.formInputMeta.name,
      "data-active-element-match-name": props.formInputMeta.marketoName
    };
  });
</script>

<template>
  <template v-if="withoutSyncWrapper !== true">
    <MarketoInputSyncWrapper v-model="proxyModelValue" :marketo-input-name="formInputMeta.marketoName" />
  </template>
  <div :id="'parent_' + formInputMeta.marketoName" class="parent-field-wrpr " :data-type="formInputMeta.type" :class="parentWrapperClasses">
    <label class="eclipse-label" :for="formInputMeta.name">
      {{ translate(FormLabelKeysEnum[formInputMeta.name], { ns: TranslationNamespaceEnum.formLabels }) }}
    </label>
    <slot name="input">
      <template v-if="formInputMeta.type === 'select'">
        <select v-model="proxyModelValue" v-bind="commonInputProps">
          <option v-for="key of dropdownOptionKeys" :key="key" :value="key">
            {{ translate(key, { ns: dropdownTranslationsNamespace }) }}
          </option>
        </select>
        <div class="drop-arrow"></div>
      </template>
      <template v-else-if="formInputMeta.type === 'textarea' && !isBoolean(proxyModelValue)">
        <textarea v-model="proxyModelValue" v-bind="commonInputProps">
        </textarea>
      </template>
      <template v-else>
        <input v-model="proxyModelValue" v-bind="commonInputProps" :type="formInputMeta.type">
      </template>
    </slot>
    <div class="error-msg">
      {{ translate(formInputMeta.name + "_Error", { ns: TranslationNamespaceEnum.formValidation }) }}
    </div>
  </div>
</template>
